import React, { useState, useEffect } from "react";
import CustomModal from "../../Global/Modal/CustomModal";
import { GOLD_PACKAGE, SILVER_PACKAGE, BRONZE_PACKAGE } from "../../../backend/PAYMENT_PACKAGES_CONSTANTS";
import "./PackageChangeModal.css";

const PackageChangeModal = ({
    isOpen,
    onClose,
    userData,
    groups = [],
    coaches = [],
    onPackageChange
}) => {
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const currentGroupsCount = groups.length;
    const currentCoachesCount = coaches.length;
    const currentPlan = userData?.paymentDetails?.plan?.toLowerCase() || "bronze";

    const packages = [
        { ...GOLD_PACKAGE, name: "gold", hebrewName: "תוכנית הזהב" },
        { ...SILVER_PACKAGE, name: "silver", hebrewName: "תוכנית הכסף" },
        { ...BRONZE_PACKAGE, name: "bronze", hebrewName: "תוכנית הארד" }
    ];

    useEffect(() => {
        if (isOpen) {
            setSelectedPackage(null);
            setErrorMessage("");
        }
    }, [isOpen]);

    const getCurrentPackage = () => {
        return packages.find(pkg => pkg.name === currentPlan);
    };

    const isPackageAvailable = (packageData) => {
        // User can't downgrade to a package that has fewer groups/coaches than they currently have
        return packageData.NUMBER_OF_GROUPS >= currentGroupsCount &&
            packageData.NUMBER_OF_COACHES >= currentCoachesCount;
    };

    const getPackageStatus = (packageData) => {
        if (packageData.name === currentPlan) {
            return { status: "current", text: "התוכנית הנוכחית", disabled: true };
        }

        if (!isPackageAvailable(packageData)) {
            return {
                status: "unavailable",
                text: `לא ניתן לעבור לתוכנית זו (יש לך ${currentGroupsCount} קבוצות ו-${currentCoachesCount} מאמנים)`,
                disabled: true
            };
        }

        if (packageData.PRICE > getCurrentPackage()?.PRICE) {
            return { status: "upgrade", text: "בחירה", disabled: false };
        } else {
            return { status: "downgrade", text: "בחירה", disabled: false };
        }
    };

    const handlePackageSelect = (packageData) => {
        const status = getPackageStatus(packageData);
        if (!status.disabled) {
            setSelectedPackage(packageData);
            setErrorMessage("");
        }
    };

    const handleConfirmChange = async () => {
        if (!selectedPackage) return;

        setIsLoading(true);
        setErrorMessage("");

        try {
            await onPackageChange(selectedPackage);
            onClose();
        } catch (error) {
            setErrorMessage(error.message || "אירעה שגיאה בעת שינוי התוכנית");
        } finally {
            setIsLoading(false);
        }
    };

    const getPackageStyle = (packageData) => {
        // Return empty object to let CSS handle the styling
        return {};
    };

    const footerButtons = [
        {
            title: "ביטול",
            style: "grey",
            onClick: onClose,
            disabled: isLoading
        },
        {
            title: isLoading ? "מעדכן..." : "אשר שינוי",
            style: "primary",
            onClick: handleConfirmChange,
            disabled: !selectedPackage || isLoading
        }
    ];

    return (
        <CustomModal
            isOpen={isOpen}
            onClose={onClose}
            title="שינוי תוכנית חבילה"
            subtitle="בחר תוכנית חדשה עבור הארגון שלך"
            width={80}
            height={85}
            footerButtons={footerButtons}
            formErrorMessage={errorMessage}
        >
            <div className="package-change-content">
                {/* Current Usage Summary */}
                <div className="current-usage-section">
                    <h4>שימוש נוכחי</h4>
                    <div className="usage-stats">
                        <div className="usage-stat">
                            <span className="stat-label">קבוצות:</span>
                            <span className="stat-value">{currentGroupsCount}</span>
                        </div>
                        <div className="usage-stat">
                            <span className="stat-label">מאמנים:</span>
                            <span className="stat-value">{currentCoachesCount}</span>
                        </div>
                    </div>
                </div>

                {/* Available Packages */}
                <div className="packages-section">
                    <h4>תוכניות זמינות</h4>
                    <div className="packages-grid">
                        {packages.map((packageData) => {
                            const status = getPackageStatus(packageData);
                            const isSelected = selectedPackage?.name === packageData.name;

                            return (
                                <div
                                    key={packageData.name}
                                    className={`package-card ${packageData.name} ${status.status} ${isSelected ? 'selected' : ''}`}
                                    style={getPackageStyle(packageData)}
                                    onClick={() => !status.disabled && handlePackageSelect(packageData)}
                                >
                                    <div className="package-header">
                                        <h5>{packageData.hebrewName}</h5>
                                        <span className="package-price">₪{packageData.PRICE}/חודש</span>
                                    </div>

                                    <div className="package-features">
                                        <div className="feature">
                                            <span className="feature-label">קבוצות:</span>
                                            <span className="feature-value">{packageData.NUMBER_OF_GROUPS}</span>
                                        </div>
                                        <div className="feature">
                                            <span className="feature-label">מאמנים:</span>
                                            <span className="feature-value">{packageData.NUMBER_OF_COACHES}</span>
                                        </div>
                                    </div>

                                    <div className="package-status">
                                        <span className="status-badge">{status.text}</span>
                                    </div>

                                    {isSelected && (
                                        <div className="selected-indicator">
                                            <span>✓</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Change Summary */}
                {selectedPackage && (
                    <div className="change-summary">
                        <h4>סיכום השינוי</h4>
                        <div className="summary-content">
                            <p>
                                <strong>מעבר מ:</strong> {getCurrentPackage()?.hebrewName} (₪{getCurrentPackage()?.PRICE}/חודש)
                            </p>
                            <p>
                                <strong>מעבר ל:</strong> {selectedPackage.hebrewName} (₪{selectedPackage.PRICE}/חודש)
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default PackageChangeModal;
